import * as React from "react";
import { Badge } from "./badge";
import { Checkbox } from "./checkbox";
import { Input } from "./input";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import { ScrollArea } from "./scroll-area";
import { Search, ChevronDown, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useLingui } from "@lingui/react";

export interface TagOption {
  value: string;
  label: string;
  color?: string;
}

interface TagSelectorProps {
  value?: string[];
  onChange?: (value: string[]) => void;
  options?: TagOption[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
  autoFocus?: boolean;
  maxHeight?: number;
}

// Sub-component for rendering tag badges
interface TagBadgeProps {
  option: TagOption;
  onRemove?: (value: string, event: React.MouseEvent) => void;
  disabled?: boolean;
  showRemoveButton?: boolean;
}

const TagBadge: React.FC<TagBadgeProps> = ({
  option,
  onRemove,
  disabled,
  showRemoveButton = true,
}) => {
  const { i18n } = useLingui();
  return (
  <Badge
    variant="secondary"
    className={cn(
      "flex items-center gap-1",
      showRemoveButton && "max-w-[150px]"
    )}
    style={{
      backgroundColor: option.color ? `${option.color}20` : undefined,
      borderColor: option.color || undefined,
      color: option.color || undefined,
    }}
  >
    <span className="truncate">{option.label}</span>
    {!disabled && showRemoveButton && onRemove && (
      <button
        type="button"
        className="ml-1 rounded-full outline-none ring-offset-background focus:ring-2 focus:ring-ring focus:ring-offset-2 hover:bg-muted/20"
        onClick={(e) => onRemove(option.value, e)}
        aria-label={i18n._({ id: "common.removeLabel", message: "Remove {label}" }, { label: option.label })}
      >
        <X className="h-3 w-3" aria-hidden="true" />
      </button>
    )}
  </Badge>
  );
};

// Sub-component for search input
interface TagSearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

const TagSearchInput = React.forwardRef<HTMLInputElement, TagSearchInputProps>(
  ({ value, onChange, placeholder }, ref) => {
    const { i18n } = useLingui();
    const label =
      placeholder ?? i18n._({ id: "tagSelector.searchPlaceholder", message: "Search tags..." });
    return (
      <div className="p-3 border-b">
        <div className="relative">
          <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" aria-hidden="true" />
          <Input
            ref={ref}
            placeholder={label}
            aria-label={label}
            value={value}
            onChange={(e) => onChange(e.target.value)}
            className="pl-8"
          />
        </div>
      </div>
    );
  }
);

TagSearchInput.displayName = "TagSearchInput";

// Sub-component for dropdown content
interface TagDropdownProps {
  options: TagOption[];
  selectedValues: string[];
  onSelect: (value: string, checked: boolean) => void;
  searchTerm: string;
  onSearchChange: (value: string) => void;
  maxHeight: number;
  autoFocus?: boolean;
}

const TagDropdown = React.forwardRef<HTMLDivElement, TagDropdownProps>(
  (
    {
      options,
      selectedValues,
      onSelect,
      searchTerm,
      onSearchChange,
      maxHeight,
      autoFocus,
    },
    ref
  ) => {
    const { i18n } = useLingui();
    const searchInputRef = React.useRef<HTMLInputElement>(null);

    // Auto-focus search input when dropdown opens
    React.useEffect(() => {
      if (autoFocus && searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, [autoFocus]);

    // Filter options based on search term
    const filteredOptions = React.useMemo(
      () =>
        options.filter(
          (opt) =>
            opt.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
            opt.value.toLowerCase().includes(searchTerm.toLowerCase())
        ),
      [options, searchTerm]
    );

    const renderOptionItem = (option: TagOption) => (
      <div
        key={option.value}
        className="flex items-center space-x-3 px-3 py-2 hover:bg-muted/50 cursor-pointer"
        onClick={() => {
          const isCurrentlySelected = selectedValues.includes(option.value);
          onSelect(option.value, !isCurrentlySelected);
        }}
      >
        <Checkbox
          checked={selectedValues.includes(option.value)}
          onCheckedChange={(checked) => onSelect(option.value, !!checked)}
          onClick={(e) => e.stopPropagation()}
          className={cn(
            "flex-shrink-0",
            !selectedValues.includes(option.value) &&
              "border-muted-foreground/50 data-[state=unchecked]:border-muted-foreground/50"
          )}
        />
        <div className="flex items-center space-x-2 min-w-0 flex-1">
          <TagBadge option={option} showRemoveButton={false} />
        </div>
      </div>
    );

    return (
      <div ref={ref}>
        <TagSearchInput
          ref={searchInputRef}
          value={searchTerm}
          onChange={onSearchChange}
        />
        <ScrollArea style={{ height: `${maxHeight}px` }}>
          {filteredOptions.length > 0 ? (
            <div>{filteredOptions.map(renderOptionItem)}</div>
          ) : (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              {i18n._({ id: "tagSelector.empty", message: "No tags found" })}
            </div>
          )}
        </ScrollArea>
      </div>
    );
  }
);

TagDropdown.displayName = "TagDropdown";

const TagSelector = React.forwardRef<HTMLDivElement, TagSelectorProps>(
  (props, ref) => {
    const {
      value = [],
      onChange,
      options = [],
      placeholder,
      className,
      disabled,
      autoFocus = false,
      maxHeight = 200,
    } = props;

    // Ensure value is always an array to prevent runtime errors
    const safeValue = React.useMemo(() => {
      if (!value) return [];
      if (Array.isArray(value)) return value;
      return [];
    }, [value]);

    const { i18n } = useLingui();
    const [open, setOpen] = React.useState(false);
    const [searchTerm, setSearchTerm] = React.useState("");

    // Generate unique IDs for accessibility
    const popoverContentId = React.useId();

    // Close popover if parent component requests it
    React.useEffect(() => {
      const handleEscape = (event: KeyboardEvent) => {
        if (event.key === "Escape" && open) {
          setOpen(false);
        }
      };

      if (open) {
        document.addEventListener("keydown", handleEscape);
        return () => document.removeEventListener("keydown", handleEscape);
      }
    }, [open]);

    const handleSelect = (optionValue: string, checked: boolean) => {
      if (checked) {
        // Guard against duplicate selections
        onChange?.(
          safeValue.includes(optionValue)
            ? safeValue
            : [...safeValue, optionValue]
        );
      } else {
        onChange?.(safeValue.filter((v) => v !== optionValue));
      }
    };

    const handleRemoveTag = (optionValue: string, event: React.MouseEvent) => {
      event.preventDefault();
      event.stopPropagation();
      onChange?.(safeValue.filter((v) => v !== optionValue));
    };

    const selectedOptions = React.useMemo(() => {
      return options.filter((opt) => safeValue.includes(opt.value));
    }, [safeValue, options]);

    return (
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <div
            ref={ref}
            role="combobox"
            aria-expanded={open}
            aria-controls={popoverContentId}
            aria-haspopup="listbox"
            className={cn(
              "flex min-h-10 w-full cursor-pointer items-center justify-between rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50",
              className
            )}
            onClick={() => !disabled && setOpen(true)}
          >
            <div className="flex flex-wrap gap-1 flex-1 overflow-hidden">
              {selectedOptions.length > 0 ? (
                selectedOptions.map((option) => (
                  <TagBadge
                    key={option.value}
                    option={option}
                    onRemove={handleRemoveTag}
                    disabled={disabled}
                  />
                ))
              ) : (
                <span className="text-muted-foreground truncate">
                  {placeholder ??
                    i18n._({ id: "tagSelector.placeholder", message: "Select tags..." })}
                </span>
              )}
            </div>
            <ChevronDown className="ml-2 h-4 w-4 shrink-0 opacity-50" aria-hidden="true" />
          </div>
        </PopoverTrigger>
        <PopoverContent
          id={popoverContentId}
          role="listbox"
          className="w-[--radix-popover-trigger-width] p-0"
          align="start"
        >
          <TagDropdown
            options={options}
            selectedValues={safeValue}
            onSelect={handleSelect}
            searchTerm={searchTerm}
            onSearchChange={setSearchTerm}
            maxHeight={maxHeight}
            autoFocus={open && autoFocus}
          />
        </PopoverContent>
      </Popover>
    );
  }
);

TagSelector.displayName = "TagSelector";

export { TagSelector };
